import type { IVersionCheckConfig } from '../config/index';
import { DEFAULT_VERSION_UPDATE_SNOOZE_MS } from './versionUpdate';

// Проверка версии вкладки: сверяет версию сборки с `version.json` на сервере и при расхождении предлагает обновиться
// (`onNewVersion` — обычно `offer` экземпляра `createVersionUpdate`). Проверка идёт по таймеру и при возврате на
// вкладку, только пока вкладка видна; отложенное «Позже» предложение покажется снова, когда истечёт отсрочка.

export const DEFAULT_VERSION_CHECK_INTERVAL_MS = 120 * 1000;

/** Сборка с версией: числовая метка (unix-время сборки или APP_VERSION окружения). В dev — `'dev'`, сравнивать не с чем. */
export const isVersionedBuild = (version: string): boolean => /^\d+$/.test(version);

/**
 * Адрес `version.json` рядом с index.html приложения; метка времени — мимо кэшей. Относительный: `<base href="/">`
 * index.html приводит его к корню (`./controlpanel/version.json` → `/controlpanel/version.json`).
 */
export const versionJsonUrl = (basePath: string, now: number = Date.now()): string =>
    `.${basePath === '/' ? '/' : basePath + '/'}version.json?t=${now}`;

/** Версия из `version.json` (`{ "version": "…" }`) строкой; не прочиталась — `null` (стенд перезапускается и т. п.). */
export async function fetchVersionJson(url: string): Promise<string | null> {
    try {
        const response = await fetch(url, {
            method: 'GET',
            cache: 'no-cache',
            headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                Pragma: 'no-cache',
                Expires: '0',
            },
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = (await response.json()) as { version?: unknown } | null;
        const version = data?.version;
        return typeof version === 'string' || typeof version === 'number' ? String(version) : null;
    } catch (error) {
        console.warn('Failed to fetch version info:', error);
        return null;
    }
}

export interface IVersionCheckSettings {
    enabled: boolean;
    intervalMs: number;
    snoozeMs: number;
}

/**
 * Настройки проверки из раздела `versionCheck` конфига приложения. Конфиг недоступен (чтение бросило) — проверка
 * выключена, умолчания: опрос раз в 120 с, отсрочка 30 минут.
 */
export function readVersionCheckSettings(read: () => IVersionCheckConfig | undefined): IVersionCheckSettings {
    let section: IVersionCheckConfig | undefined;
    try {
        section = read();
    } catch {
        console.warn('Config not available for version check, defaulting to disabled, interval to 120s');
        section = { versionCheckEnabled: false };
    }
    const minutes = section?.snoozeMinutes;
    return {
        enabled: section?.versionCheckEnabled ?? false,
        intervalMs: section?.versionCheckInterval ?? DEFAULT_VERSION_CHECK_INTERVAL_MS,
        snoozeMs: minutes && minutes > 0 ? minutes * 60 * 1000 : DEFAULT_VERSION_UPDATE_SNOOZE_MS,
    };
}

export interface IVersionCheckOptions {
    enabled: boolean;
    intervalMs: number;
    /** Версия этой сборки (`__APP_VERSION__` приложения). Без версии (dev) — первая прочитанная с сервера. */
    buildVersion: string;
    /** Адрес `version.json` — на каждую проверку свой (метка времени): `() => versionJsonUrl(BASE_PATH)`. */
    versionUrl: () => string;
    /** Версия на сервере отличается от версии вкладки — на каждой такой проверке. */
    onNewVersion: () => void;
}

export interface IVersionCheck {
    /** Таймер и подписка на видимость вкладки; повторный вызов — no-op. Выключенная проверка не запускается. */
    readonly start: () => void;
    /** Снять таймер и подписку. */
    readonly stop: () => void;
}

export function createVersionCheck({
    enabled,
    intervalMs,
    buildVersion,
    versionUrl,
    onNewVersion,
}: IVersionCheckOptions): IVersionCheck {
    // Версия, с которой работает вкладка: зашитая в сборку, а без неё — первая прочитанная с сервера. Неудачное
    // чтение базу не задаёт: её задаст первое удачное (иначе проверка была бы мертва до перезагрузки).
    let runningVersion: string | null = isVersionedBuild(buildVersion) ? buildVersion : null;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let started = false;

    const isTabActive = () => document.visibilityState === 'visible';

    async function check(): Promise<void> {
        const serverVersion = await fetchVersionJson(versionUrl());
        if (serverVersion === null) return;
        if (runningVersion === null) {
            runningVersion = serverVersion;
            return;
        }
        if (serverVersion !== runningVersion) onNewVersion();
    }

    const onVisibilityChange = () => {
        if (isTabActive()) void check();
    };

    return {
        start() {
            if (!enabled) {
                console.log('Version checking is disabled');
                return;
            }
            if (started) return;
            started = true;
            if (runningVersion === null) void check();
            intervalId = setInterval(() => {
                if (isTabActive()) void check();
            }, intervalMs);
            document.addEventListener('visibilitychange', onVisibilityChange);
        },
        stop() {
            if (intervalId !== null) clearInterval(intervalId);
            intervalId = null;
            document.removeEventListener('visibilitychange', onVisibilityChange);
            started = false;
        },
    };
}
